import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

/* ================= CONFIG ================= */
// ✅ FIXED
const API = process.env.REACT_APP_API_URL || "/api";

function AddDevice() {
  const params = useParams();
  const deviceParam = params.uid || params.deviceId;
  const navigate = useNavigate();

  const isEdit = !!deviceParam;

  const [form, setForm] = useState({
    device_uid: "",
    product_id: "",
    company_id: "",
    status: "active",
  });

  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deviceDbId, setDeviceDbId] = useState(null);

  const token = localStorage.getItem("token");
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const canChooseCompany = user.role === "super_admin" || user.role === "system_manager";

  /* ================= FETCH PRODUCTS ================= */
  useEffect(() => {
    const fetchProducts = async () => {
      if (!token) return;

      const res = await fetch(`${API}/products`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json();

      const safeData = Array.isArray(data)
        ? data
        : data.rows || data.data || [];

      setProducts(safeData);
    };

    fetchProducts();
  }, [token]);

  /* ================= FETCH COMPANIES ================= */
  useEffect(() => {
    if (!canChooseCompany) return;

    const fetchCompanies = async () => {
      const res = await fetch(`${API}/companies`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();
      setCompanies(data);
    };

    fetchCompanies();
  }, [canChooseCompany, token]);

  /* ================= FETCH DEVICE (EDIT) ================= */
  useEffect(() => {
    if (!isEdit) return;

    const fetchDevice = async () => {
      setLoading(true);

      const res = await fetch(`${API}/devices/uid/${encodeURIComponent(deviceParam)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      setForm({
        device_uid: data.device_uid,
        product_id: data.product_id || "",
        company_id: data.company_id || "",
        status: data.status,
      });
      setDeviceDbId(data.id);

      setLoading(false);
    };

    fetchDevice();
  }, [deviceParam, isEdit, token]);

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const url = isEdit
      ? `${API}/devices/${deviceDbId}`
      : `${API}/devices`;

    const method = isEdit ? "PUT" : "POST";

    const payload = {
      device_uid: form.device_uid,
      product_id: form.product_id,
      status: form.status,
    };

    if (canChooseCompany) {
      payload.company_id = form.company_id || null;
    }

    try {
      if (isEdit && !deviceDbId) {
        alert("Device record is still loading. Please try again.");
        setLoading(false);
        return;
      }

      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        alert(data.message || "Failed to save device");
        setLoading(false);
        return;
      }

      navigate("/dashboard/devices");

    } catch (err) {
      console.error("ERROR:", err);
      alert("Network error");
    }

    setLoading(false);
  };

  return (
    <div className="p-6 max-w-2xl">

      <h2 className="text-2xl font-semibold mb-2">
        {isEdit ? "Edit Device" : "Add New Device"}
      </h2>

      {loading && <div className="mb-4 text-sm">Loading...</div>}

      <form onSubmit={handleSubmit} className="space-y-4">

        {/* Device UID */}
        <div>
          <label className="block mb-1 text-sm">Device UID</label>
          <input
            value={form.device_uid}
            onChange={(e) =>
              setForm({ ...form, device_uid: e.target.value })
            }
            className="w-full border rounded-lg p-2"
            required
          />
        </div>

        {/* PRODUCT */}
        <div>
          <label className="block mb-1 text-sm">Product</label>
          <select
            value={form.product_id}
            onChange={(e) =>
              setForm({ ...form, product_id: e.target.value })
            }
            className="w-full border rounded-lg p-2"
            required
          >
            <option value="">Select Product</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        {/* Company */}
        {canChooseCompany && (
          <div>
            <label className="block mb-1 text-sm">Company</label>
            <select
              value={form.company_id}
              onChange={(e) =>
                setForm({ ...form, company_id: e.target.value })
              }
              className="w-full border rounded-lg p-2"
            >
              <option value="">Unassigned</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company_name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Status */}
        <div>
          <label className="block mb-1 text-sm">Status</label>
          <select
            value={form.status}
            onChange={(e) =>
              setForm({ ...form, status: e.target.value })
            }
            className="w-full border rounded-lg p-2"
          >
            <option value="active">Active</option>
            <option value="maintenance">Maintenance</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-4 mt-6">
          <button
            type="button"
            onClick={() => navigate("/dashboard/devices")}
            className="px-4 py-2 border rounded-lg"
          >
            Cancel
          </button>

          <button
            type="submit"
            className="bg-blue-600 text-white px-5 py-2 rounded-lg"
          >
            {isEdit ? "Update Device" : "Save Device"}
          </button>
        </div>

      </form>
    </div>
  );
}

export default AddDevice;
